import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from 'antd';
import { TagChip } from '../../../components/entity-detail/TagChip.js';
import { QuietEmpty } from '../../../shared/ui/quiet-states.js';
import { LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';

import type { ContactItem } from '../api/contacts.client.js';

export interface ContactCardsGridProps {
  contacts: ContactItem[];
  loading?: boolean;
}

/**
 * Карточный (grid) режим списка контактов. Те же серверные фильтры и выборка,
 * что у таблицы, — компонент только раскладывает контакты по карточкам.
 * Каждая карточка — ссылка на карточку контакта. Warm Precision: L14-плитки
 * на hairline-бордере, hover — заливка surface-2, без теней.
 */
export function ContactCardsGrid({
  contacts,
  loading = false,
}: ContactCardsGridProps): ReactElement {
  useI18nSubscription();

  if (loading) {
    return (
      <div
        data-testid="contacts-cards-grid-loading"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
      >
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="crm-radius-l p-4" style={LIST_SURFACE_STYLE}>
            <Skeleton active avatar paragraph={{ rows: 2 }} />
          </div>
        ))}
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div
        data-testid="contacts-cards-grid-empty"
        className="flex min-h-[240px] items-center justify-center"
        style={LIST_SURFACE_STYLE}
      >
        <QuietEmpty text={i18n.t('crm.contacts.empty')} />
      </div>
    );
  }

  return (
    <div
      data-testid="contacts-cards-grid"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      {contacts.map((contact) => (
        <Link
          key={contact.id}
          to={`/contacts/${contact.id}`}
          data-testid={`contact-card-${contact.id}`}
          className="crm-hover-fill crm-radius-l block border border-[var(--ant-color-border-secondary)] bg-[var(--ant-color-bg-container)] p-3 text-sm transition-colors hover:border-[var(--ant-color-border)]"
        >
          <h4
            className="mb-1 font-semibold text-[var(--ant-color-text)]"
            style={{ fontSize: 13, lineHeight: 1.3, wordBreak: 'break-word' }}
          >
            {contact.fullName}
          </h4>
          <div className="truncate text-xs text-[var(--ant-color-text-secondary)]">
            {contact.companyRef?.name ??
              i18n.t('crm.features.features.contacts.pages.contactspage.частное_лицо')}
          </div>
          <div className="mt-1.5 truncate text-xs text-[var(--ant-color-text-secondary)]">
            {contact.phone ?? contact.email ?? '—'}
          </div>
          {contact.tags.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {contact.tags.map((tag) => (
                <TagChip key={tag} value={tag} />
              ))}
            </div>
          ) : null}
        </Link>
      ))}
    </div>
  );
}
